/**
 * Poster frames for video clips.
 *
 * Clips share their source's file, so without this every clip cut from one game would show
 * the same thumbnail. We seek an off-screen video element to each clip's start and grab the
 * frame there.
 *
 * Two deliberate constraints:
 *
 * 1. This uses its OWN video element with `crossOrigin="anonymous"`, never the visible
 *    player's. Reading pixels off a cross-origin video taints the canvas, so the element has
 *    to be CORS-enabled — but a bucket without CORS configured will refuse to load a
 *    crossOrigin element at all. Keeping it separate means a missing bucket CORS rule costs
 *    us thumbnails, not playback.
 * 2. Every failure resolves to `null` instead of throwing. A poster frame is a nicety; losing
 *    one must never fail the clip the user just spent time marking out.
 */

const METADATA_TIMEOUT_MS = 30000;
const SEEK_TIMEOUT_MS = 10000;
const JPEG_QUALITY = 0.8;

/** Longest edge of a captured poster frame. Tiles are small; full-res frames are wasted bytes. */
const MAX_THUMBNAIL_EDGE = 640;

const computeSize = (
  videoWidth: number,
  videoHeight: number
): { width: number; height: number } => {
  const longestEdge = Math.max(videoWidth, videoHeight);
  if (longestEdge <= MAX_THUMBNAIL_EDGE) {
    return { width: videoWidth, height: videoHeight };
  }

  const scale = MAX_THUMBNAIL_EDGE / longestEdge;
  return {
    width: Math.round(videoWidth * scale),
    height: Math.round(videoHeight * scale),
  };
};

/**
 * Captures one frame per requested timestamp from a remote video.
 *
 * @param videoUrl - Signed URL of the source video
 * @param timesSeconds - Offsets to capture, in seconds from the start of the video
 * @returns One entry per requested time, in the same order. `null` where capture failed.
 */
export const captureClipPosterFrames = async (
  videoUrl: string,
  timesSeconds: number[]
): Promise<(Blob | null)[]> => {
  if (timesSeconds.length === 0) return [];

  const empty = timesSeconds.map(() => null);

  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.warn('[VideoClipThumbnail] Canvas context unavailable; skipping poster frames');
    return empty;
  }

  try {
    await new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(
        () => reject(new Error('Timed out loading video metadata')),
        METADATA_TIMEOUT_MS
      );

      video.onloadedmetadata = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };
      video.onerror = () => {
        window.clearTimeout(timeoutId);
        // Most likely cause: the bucket has no CORS rule for this origin.
        reject(new Error('Failed to load video for poster frame capture'));
      };

      video.src = videoUrl;
    });
  } catch (error) {
    console.warn('[VideoClipThumbnail] Could not open video; clips will have no poster frames', {
      error,
    });
    return empty;
  }

  const { width, height } = computeSize(video.videoWidth, video.videoHeight);
  canvas.width = width;
  canvas.height = height;

  const frames: (Blob | null)[] = [];

  for (const time of timesSeconds) {
    try {
      await new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(
          () => reject(new Error(`Timed out seeking to ${time}s`)),
          SEEK_TIMEOUT_MS
        );

        video.onseeked = () => {
          window.clearTimeout(timeoutId);
          resolve();
        };
        video.onerror = () => {
          window.clearTimeout(timeoutId);
          reject(new Error(`Failed to seek to ${time}s`));
        };

        // Clamp inside the media: seeking past the end never fires `seeked` on some browsers.
        const duration = Number.isFinite(video.duration) ? video.duration : time;
        video.currentTime = Math.max(0, Math.min(time, Math.max(0, duration - 0.05)));
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
      });

      frames.push(blob);
    } catch (error) {
      // A tainted canvas throws here (SecurityError) — as does a seek failure. Either way the
      // clip is still valid, it just won't have its own poster frame.
      console.warn('[VideoClipThumbnail] Poster frame capture failed', { error, time });
      frames.push(null);
    }
  }

  // Release the buffered media; without this the element can hold the download open.
  video.removeAttribute('src');
  video.load();

  return frames;
};
